import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { invalidateAppConfig } from "../lib/useAppConfig";
import {
  Settings2,
  Plus,
  Pencil,
  Trash2,
  Save,
  Search,
  RefreshCw,
  ChevronLeft,
  AlertTriangle,
  Archive,
  Lock,
  CheckSquare,
  Square,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";

const KIND_META = [
  { kind: "project_station", title: "Project / Station", subtitle: "Substations CSV is the canonical list — admin-added extras here.", isStation: true },
  { kind: "current_location", title: "Current Location", subtitle: "Where the shipment is right now" },
  { kind: "storage_detail", title: "Stocking Location / Yard Storage", subtitle: "Single source of truth — bays, racks, and the 4 approved yard storage locations." },
  { kind: "priority", title: "Priority", subtitle: "Used on every shipment + crew request" },
  { kind: "status", title: "Status", subtitle: "Lifecycle status across the whole app" },
  { kind: "prep_queue_status", title: "Preparation Queue Status", subtitle: "Drives the 3-tab Runner Queue" },
  { kind: "item_type", title: "Inventory Item Type", subtitle: "Used by the Inventory Items table" },
  { kind: "supervisor", title: "Supervisor Name", subtitle: "Assignable to users; auto-stamped on Tool Requests for strict tenancy." },
];

export default function AdminDropdowns() {
  const { isPrivileged } = useAuth();
  const nav = useNavigate();
  const [activeKind, setActiveKind] = useState(KIND_META[0].kind);
  const [data, setData] = useState({}); // { kind: { rows: [...] } }
  const [usage, setUsage] = useState({}); // { kind: { option_id: count } }
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setRefreshing(true);
    setError("");
    try {
      const out = {};
      const usageOut = {};
      for (const m of KIND_META) {
        const [{ data: d }, usageRes] = await Promise.all([
          api.get(`/app-config/${m.kind}`),
          api.get(`/app-config/${m.kind}/usage`).catch(() => ({ data: {} })),
        ]);
        out[m.kind] = d;
        usageOut[m.kind] = usageRes.data || {};
      }
      setData(out);
      setUsage(usageOut);
      invalidateAppConfig();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Failed to load dropdowns");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!isPrivileged) {
      nav("/", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivileged]);

  if (!isPrivileged) return null;

  const activeMeta = KIND_META.find((k) => k.kind === activeKind);
  const activeData = data[activeKind];
  const activeUsage = usage[activeKind] || {};

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <button
        onClick={() => nav(-1)}
        data-testid="back-btn"
        className="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-slate-600 hover:text-slate-900 mb-3"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            Admin / Admin · App Configuration
          </div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase text-slate-900 tracking-tight">
            Manage Dropdowns
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Add, rename, archive or restore options for every dropdown in the app. Changes appear instantly across Receive, Queue, Inventory and forms.
          </p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid="dropdowns-refresh-btn"
          className="rg-tap px-4 border-2 border-slate-300 hover:border-slate-900 rounded-sm flex items-center gap-2 font-bold uppercase tracking-wide text-slate-700 bg-white"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="rg-card p-4 mb-4 bg-amber-50 border-2 border-amber-300 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
        <div className="text-sm text-amber-900">
          <strong>Note:</strong> Renaming an option auto-renames every shipment / inventory record using it.
          Deleting an option used by records will <strong>archive</strong> it instead (records keep their data, but it disappears from new dropdowns).
          The <strong>Project / Station</strong> tab covers admin-added extras only — the bulk Substations list is sourced from the canonical CSV.
        </div>
      </div>

      {error && (
        <div className="rg-card p-3 mb-4 border-2 border-rose-600 bg-rose-50 text-rose-800 text-sm font-bold">
          {error}
        </div>
      )}

      {/* Tab strip — kinds */}
      <div className="flex flex-wrap gap-1.5 mb-4 p-1 bg-slate-100 border-2 border-slate-200 rounded-sm" data-testid="dropdowns-kind-tabs">
        {KIND_META.map((m) => {
          const active = m.kind === activeKind;
          const count = data[m.kind]?.rows?.filter((r) => !r.archived).length ?? 0;
          return (
            <button
              key={m.kind}
              onClick={() => setActiveKind(m.kind)}
              data-testid={`dropdowns-tab-${m.kind}`}
              className={`rg-tap px-3 rounded-sm font-bold uppercase tracking-wide text-xs flex items-center gap-1.5 ${
                active ? "bg-slate-900 text-white" : "bg-transparent text-slate-700 hover:bg-white"
              }`}
            >
              {m.title}
              <span
                className={`min-w-[22px] px-1 text-[10px] rounded-sm ${
                  active ? "bg-amber-500 text-slate-900" : "bg-slate-300 text-slate-700"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : activeData ? (
        <DropdownEditor
          key={activeKind}
          kind={activeKind}
          title={activeMeta.title}
          subtitle={activeMeta.subtitle}
          isStation={!!activeMeta.isStation}
          data={activeData}
          usageMap={activeUsage}
          onChanged={load}
        />
      ) : null}
    </div>
  );
}

function DropdownEditor({ kind, title, subtitle, isStation, data, usageMap, onChanged }) {
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  // Compound fields for project_station
  const [newStationNumber, setNewStationNumber] = useState("");
  const [newStationName, setNewStationName] = useState("");
  const [editId, setEditId] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [busyId, setBusyId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // {row, in_use_count}
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const allRows = (data?.rows || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return allRows;
    return allRows.filter((r) => {
      if ((r.label || "").toLowerCase().includes(needle)) return true;
      const ex = r.extra || {};
      if ((ex.station_number || "").toLowerCase().includes(needle)) return true;
      if ((ex.station_name || "").toLowerCase().includes(needle)) return true;
      return false;
    });
  }, [allRows, search]);

  const stationCompound = (num, name) => {
    const n = (num || "").trim();
    const nm = (name || "").trim();
    if (n && nm) return `${n} — ${nm}`;
    return n || nm;
  };

  const addOption = async () => {
    let label;
    let extra = null;
    if (isStation) {
      label = stationCompound(newStationNumber, newStationName);
      if (!label) {
        toast.error("Station number or name is required");
        return;
      }
      extra = { station_number: newStationNumber.trim(), station_name: newStationName.trim() };
    } else {
      label = newLabel.trim();
      if (!label) return;
    }
    setBusyId("__new__");
    try {
      await api.post(`/app-config/${kind}`, { label, extra });
      toast.success(`Added "${label}"`);
      setNewLabel("");
      setNewStationNumber("");
      setNewStationName("");
      setCreating(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add");
    } finally {
      setBusyId("");
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditLabel(row.label);
  };

  const saveEdit = async (row) => {
    const label = editLabel.trim();
    if (!label) return;
    if (label === row.label) {
      setEditId("");
      return;
    }
    setBusyId(row.id);
    try {
      await api.patch(`/app-config/${kind}/${row.id}`, { label });
      toast.success(`Renamed → "${label}"`);
      setEditId("");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setBusyId("");
    }
  };

  const askDelete = async (row) => {
    setBusyId(row.id);
    try {
      const { data: u } = await api.get(`/app-config/${kind}/${row.id}/usage`);
      setConfirmDelete({ row, in_use_count: u.in_use_count });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusyId("");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { row, in_use_count } = confirmDelete;
    setBusyId(row.id);
    try {
      const force = in_use_count > 0;
      await api.delete(`/app-config/${kind}/${row.id}${force ? "?force=true" : ""}`);
      toast.success(force ? `Archived "${row.label}"` : `Deleted "${row.label}"`);
      setConfirmDelete(null);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    } finally {
      setBusyId("");
    }
  };

  const unarchive = async (row) => {
    setBusyId(row.id);
    try {
      await api.patch(`/app-config/${kind}/${row.id}`, { archived: false });
      toast.success(`Restored "${row.label}"`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusyId("");
    }
  };

  // Bulk select helpers
  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const allVisibleIds = filtered.map((r) => r.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selected.has(id));
  const toggleAll = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allSelected) {
        for (const id of allVisibleIds) n.delete(id);
      } else {
        for (const id of allVisibleIds) n.add(id);
      }
      return n;
    });
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data: r } = await api.post(`/app-config/${kind}/bulk`, { option_ids: ids, action });
      const archived = r.archived || 0;
      const restored = r.restored || 0;
      const deleted = r.deleted || 0;
      const autoArc = r.auto_archived_due_to_usage || 0;
      let msg = "";
      if (action === "archive") msg = `Archived ${archived} option${archived === 1 ? "" : "s"}`;
      else if (action === "restore") msg = `Restored ${restored} option${restored === 1 ? "" : "s"}`;
      else {
        msg = `Deleted ${deleted}, archived ${autoArc} (in use)`;
      }
      toast.success(msg);
      clearSelection();
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const usageCount = (id) => usageMap[id] ?? 0;

  return (
    <div className="rg-card p-5" data-testid={`dropdown-editor-${kind}`}>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-display text-lg uppercase text-slate-900 leading-tight">{title}</h2>
          <p className="text-xs text-slate-600 mt-0.5">{subtitle}</p>
        </div>
        <button
          onClick={() => {
            setCreating(true);
            setNewLabel("");
            setNewStationNumber("");
            setNewStationName("");
          }}
          data-testid={`dropdown-add-btn-${kind}`}
          className="rg-tap px-3 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide rounded-sm flex items-center gap-2 text-xs border-2 border-slate-900"
        >
          <Plus strokeWidth={3} className="h-4 w-4" />
          Add option
        </button>
      </div>

      {/* Search + summary */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mb-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid={`dropdown-search-${kind}`}
            placeholder={`Search ${title}…`}
            className="rg-tap w-full pl-9 pr-3 border-2 border-slate-300 rounded-sm bg-white focus:border-slate-900 outline-none"
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <span data-testid={`dropdown-summary-${kind}`}>
            <strong>{filtered.length}</strong> of <strong>{allRows.length}</strong>
            {" · "}
            <span className="text-emerald-700">In use:</span> {allRows.filter((r) => usageCount(r.id) > 0 && !r.archived).length}
            {" · "}
            <span className="text-slate-500">Unused:</span> {allRows.filter((r) => usageCount(r.id) === 0 && !r.archived).length}
            {" · "}
            <span className="text-rose-700">Archived:</span> {allRows.filter((r) => r.archived).length}
          </span>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          className="flex items-center gap-2 mb-3 px-3 py-2 bg-slate-900 text-white rounded-sm border-2 border-slate-900"
          data-testid={`dropdown-bulk-bar-${kind}`}
        >
          <CheckSquare className="h-4 w-4 text-amber-400" />
          <span className="text-sm font-bold">{selected.size} selected</span>
          <button
            onClick={() => runBulk("archive")}
            disabled={bulkBusy}
            data-testid={`dropdown-bulk-archive-${kind}`}
            className="rg-tap px-3 bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-xs rounded-sm flex items-center gap-1 border-2 border-amber-800"
          >
            <Archive className="h-3.5 w-3.5" />
            Archive
          </button>
          <button
            onClick={() => runBulk("restore")}
            disabled={bulkBusy}
            data-testid={`dropdown-bulk-restore-${kind}`}
            className="rg-tap px-3 bg-emerald-700 hover:bg-emerald-800 text-white font-bold uppercase tracking-wider text-xs rounded-sm flex items-center gap-1 border-2 border-emerald-900"
          >
            <Undo2 className="h-3.5 w-3.5" />
            Restore
          </button>
          <button
            onClick={() => runBulk("delete")}
            disabled={bulkBusy}
            data-testid={`dropdown-bulk-delete-${kind}`}
            className="rg-tap px-3 bg-rose-700 hover:bg-rose-800 text-white font-bold uppercase tracking-wider text-xs rounded-sm flex items-center gap-1 border-2 border-rose-900"
            title="Delete safe rows; rows still in use will be archived"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete / Archive
          </button>
          <button
            onClick={clearSelection}
            data-testid={`dropdown-bulk-clear-${kind}`}
            className="rg-tap px-3 ml-auto border-2 border-slate-700 hover:border-white text-slate-200 hover:text-white text-xs font-bold uppercase rounded-sm"
          >
            Clear
          </button>
        </div>
      )}

      {creating && (
        <div className="rg-card p-3 mb-3 border-2 border-emerald-400 bg-emerald-50" data-testid={`dropdown-add-row-${kind}`}>
          {isStation ? (
            <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_auto_auto] gap-2">
              <input
                value={newStationNumber}
                onChange={(e) => setNewStationNumber(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addOption()}
                placeholder="Station Number (TSS 12)"
                autoFocus
                data-testid={`dropdown-add-station-number-${kind}`}
                className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-emerald-700 outline-none font-mono"
              />
              <input
                value={newStationName}
                onChange={(e) => setNewStationName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addOption()}
                placeholder="Station Name (Smithfield)"
                data-testid={`dropdown-add-station-name-${kind}`}
                className="rg-tap border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-emerald-700 outline-none"
              />
              <button
                onClick={addOption}
                disabled={busyId === "__new__" || (!newStationNumber.trim() && !newStationName.trim())}
                data-testid={`dropdown-add-save-${kind}`}
                className="rg-tap px-3 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold uppercase rounded-sm flex items-center gap-1 border-2 border-emerald-900 disabled:opacity-50"
              >
                <Save strokeWidth={3} className="h-3.5 w-3.5" />
                Save
              </button>
              <button
                onClick={() => setCreating(false)}
                data-testid={`dropdown-add-cancel-${kind}`}
                className="rg-tap px-3 border-2 border-slate-300 hover:border-slate-900 rounded-sm text-xs font-bold uppercase text-slate-700 bg-white"
              >
                Cancel
              </button>
              <div className="sm:col-span-4 text-[11px] text-slate-600 -mt-1">
                Will display as: <strong>{stationCompound(newStationNumber, newStationName) || "—"}</strong>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addOption()}
                placeholder="New option label…"
                autoFocus
                data-testid={`dropdown-add-input-${kind}`}
                className="rg-tap flex-1 border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-emerald-700 outline-none"
              />
              <button
                onClick={addOption}
                disabled={busyId === "__new__" || !newLabel.trim()}
                data-testid={`dropdown-add-save-${kind}`}
                className="rg-tap px-3 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold uppercase rounded-sm flex items-center gap-1 border-2 border-emerald-900 disabled:opacity-50"
              >
                <Save strokeWidth={3} className="h-3.5 w-3.5" />
                Save
              </button>
              <button
                onClick={() => setCreating(false)}
                data-testid={`dropdown-add-cancel-${kind}`}
                className="rg-tap px-3 border-2 border-slate-300 hover:border-slate-900 rounded-sm text-xs font-bold uppercase text-slate-700 bg-white"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* Select-all toggle */}
      {allRows.length > 0 && (
        <div className="flex items-center gap-2 mb-1 px-1 text-[11px] text-slate-500 font-bold uppercase tracking-wider">
          <button
            onClick={toggleAll}
            data-testid={`dropdown-select-all-${kind}`}
            className="rg-tap p-1 hover:bg-slate-100 rounded-sm"
            aria-label="Select all"
          >
            {allSelected ? (
              <CheckSquare className="h-4 w-4 text-slate-900" />
            ) : (
              <Square className="h-4 w-4 text-slate-400" />
            )}
          </button>
          <span>{filtered.length} visible</span>
        </div>
      )}

      <ul className="space-y-1.5" data-testid={`dropdown-list-${kind}`}>
        {filtered.length === 0 && (
          <li className="py-6 text-center text-sm text-slate-500 italic">
            {search ? `No matches for "${search}"` : "No options yet — add one above."}
          </li>
        )}
        {filtered.map((row) => {
          const editing = editId === row.id;
          const inUse = usageCount(row.id);
          const isSelected = selected.has(row.id);
          // Color coding
          let rowTone = "border-slate-200 bg-white";
          if (row.archived) rowTone = "border-slate-200 bg-slate-50 text-slate-500";
          else if (inUse > 0) rowTone = "border-emerald-300 bg-emerald-50/40";
          else rowTone = "border-slate-200 bg-white";
          if (isSelected) rowTone = "border-amber-500 bg-amber-50";
          const ex = row.extra || {};
          return (
            <li
              key={row.id}
              data-testid={`dropdown-row-${kind}-${row.id}`}
              className={`flex items-center gap-2 px-3 py-2 border-2 rounded-sm ${rowTone}`}
            >
              <button
                onClick={() => toggleSelect(row.id)}
                data-testid={`dropdown-select-${row.id}`}
                aria-label={isSelected ? "Deselect" : "Select"}
                className="rg-tap p-1 hover:bg-white/50 rounded-sm flex-shrink-0"
              >
                {isSelected ? (
                  <CheckSquare className="h-4 w-4 text-amber-700" />
                ) : (
                  <Square className="h-4 w-4 text-slate-400" />
                )}
              </button>
              {row.is_seed && (
                <span title="Default option — created on first install" className="text-slate-400 flex-shrink-0">
                  <Lock className="h-3 w-3" />
                </span>
              )}
              {editing ? (
                <input
                  value={editLabel}
                  onChange={(e) => setEditLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveEdit(row);
                    if (e.key === "Escape") setEditId("");
                  }}
                  autoFocus
                  data-testid={`dropdown-edit-input-${row.id}`}
                  className="flex-1 border-2 border-slate-300 rounded-sm px-2 py-1 text-sm bg-white focus:border-slate-900 outline-none"
                />
              ) : (
                <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-bold ${row.archived ? "line-through" : "text-slate-900"}`}>
                    {row.label}
                  </span>
                  {isStation && ex.station_number && ex.station_name && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 font-mono">
                      {ex.station_number} · {ex.station_name}
                    </span>
                  )}
                  {row.archived ? (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-200 text-slate-600 font-bold">
                      Archived
                    </span>
                  ) : inUse > 0 ? (
                    <span
                      data-testid={`dropdown-usage-${row.id}`}
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold"
                    >
                      In use · {inUse}
                    </span>
                  ) : (
                    <span
                      data-testid={`dropdown-usage-${row.id}`}
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-500 border border-slate-200 font-bold"
                    >
                      Unused
                    </span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-1">
                {row.archived ? (
                  <button
                    onClick={() => unarchive(row)}
                    disabled={busyId === row.id}
                    data-testid={`dropdown-restore-btn-${row.id}`}
                    className="rg-tap px-2 border-2 border-emerald-300 hover:border-emerald-700 rounded-sm text-[10px] font-bold uppercase text-emerald-700 bg-white"
                  >
                    Restore
                  </button>
                ) : editing ? (
                  <>
                    <button
                      onClick={() => saveEdit(row)}
                      disabled={busyId === row.id || !editLabel.trim()}
                      data-testid={`dropdown-edit-save-${row.id}`}
                      className="rg-tap px-2 bg-emerald-700 hover:bg-emerald-800 text-white text-[10px] font-bold uppercase rounded-sm flex items-center gap-1 border-2 border-emerald-900 disabled:opacity-50"
                    >
                      <Save strokeWidth={3} className="h-3 w-3" />
                      Save
                    </button>
                    <button
                      onClick={() => setEditId("")}
                      data-testid={`dropdown-edit-cancel-${row.id}`}
                      className="rg-tap px-2 border-2 border-slate-300 hover:border-slate-900 rounded-sm text-[10px] font-bold uppercase text-slate-700 bg-white"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => startEdit(row)}
                      data-testid={`dropdown-edit-btn-${row.id}`}
                      className="rg-tap p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
                      aria-label="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => askDelete(row)}
                      disabled={busyId === row.id}
                      data-testid={`dropdown-delete-btn-${row.id}`}
                      className="rg-tap p-1.5 text-rose-600 hover:text-rose-800 hover:bg-rose-50 rounded-sm"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Delete-with-usage modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 grid place-items-center p-4"
          data-testid="dropdown-delete-modal"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-md w-full max-w-md p-5 border-2 border-rose-600"
          >
            <div className="flex items-center gap-2 mb-3">
              {confirmDelete.in_use_count > 0 ? (
                <Archive className="h-5 w-5 text-amber-700" />
              ) : (
                <Trash2 className="h-5 w-5 text-rose-700" />
              )}
              <h3 className="font-display text-lg uppercase">
                {confirmDelete.in_use_count > 0
                  ? `Archive "${confirmDelete.row.label}"?`
                  : `Delete "${confirmDelete.row.label}"?`}
              </h3>
            </div>
            {confirmDelete.in_use_count > 0 ? (
              <div className="text-sm text-slate-700 mb-4 space-y-2">
                <div className="rg-card p-3 border-2 border-amber-300 bg-amber-50 text-amber-900">
                  <strong>{confirmDelete.in_use_count}</strong> record{confirmDelete.in_use_count === 1 ? "" : "s"} currently use this option.
                  We&apos;ll <strong>archive</strong> it instead of hard-deleting so existing records keep their data and the option simply disappears from new dropdowns.
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-700 mb-4">
                No records use this option. It will be permanently deleted.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                data-testid="dropdown-delete-cancel-btn"
                className="rg-tap px-3 border-2 border-slate-300 hover:border-slate-900 rounded-sm text-sm font-bold uppercase text-slate-700 bg-white"
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                disabled={busyId === confirmDelete.row.id}
                data-testid="dropdown-delete-confirm-btn"
                className={`rg-tap px-3 text-white text-sm font-bold uppercase rounded-sm flex items-center gap-1 border-2 disabled:opacity-50 ${
                  confirmDelete.in_use_count > 0
                    ? "bg-amber-600 hover:bg-amber-700 border-amber-800"
                    : "bg-rose-700 hover:bg-rose-800 border-rose-900"
                }`}
              >
                {confirmDelete.in_use_count > 0 ? (
                  <>
                    <Archive strokeWidth={3} className="h-3.5 w-3.5" />
                    Archive
                  </>
                ) : (
                  <>
                    <Trash2 strokeWidth={3} className="h-3.5 w-3.5" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
